import * as React from 'react';
import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import classNames from 'classnames';

import { inboxZoomedInMediaSelector } from '@/redux/app/selectors/inbox';
import { toggleZoomedInMediaModal } from '@/redux/app/actions/inbox';
import { Modal } from '../../shared/Modal';

const MediaContainer = styled.div<{ maxZoom: boolean }>`
  max-width: 90vw;
  max-height: 90vh;
  overflow: scroll;
  cursor: ${({ maxZoom }) => (maxZoom ? 'zoom-out' : 'zoom-in')};
  border: 3px solid white;
`;

const maxWidth = 'calc(90vw - 6px)';
const maxHeight = 'calc(90vh - 6px)';
const MediaWrapper = styled.div<{ extraZoomed: boolean, width?: number, height?: number }>`
  border: 1px solid black;
  line-height: 0;
  ${({ width, height }) => {
    if (!height) return '';
    if (!width) return `height: ${height}px;`;
    return `height: calc(min(${width}px, ${maxWidth}) * ${height} / ${width});`;
  }}
  ${({ width, height }) => {
    if (!width) return '';
    if (!height) return `width: ${width}px;`;
    return `width: calc(min(${height}px, ${maxHeight}) * ${width} / ${height});`;
  }}
  ${({ extraZoomed }) => (
    extraZoomed
      ? css({ maxWidth: 'initial', maxHeight: 'initial' })
      : css({ maxWidth, maxHeight, overflow: 'hidden' })
  )}
`;

export const ZoomedInMediaModal = () => {
  const media = useSelector(inboxZoomedInMediaSelector);
  const type = media && media.mime.substr(0, 5);

  const [canUseExtraZoom, setCanUseExtraZoom] = useState(false);
  const [extraZoomed, setExtraZoomed] = useState(false);
  const isMaxZoom = extraZoomed || !canUseExtraZoom;

  const dispatch = useDispatch();
  const handleDismiss = useCallback(() => {
    dispatch(toggleZoomedInMediaModal(null));
    setExtraZoomed(false);
  }, [dispatch]);

  const mediaRef = useRef<any>();
  const handleLoad = useCallback(() => {
    const { naturalWidth, naturalHeight, width, height } = mediaRef.current;
    const isMediaBiggerThanContainer = naturalWidth > width || naturalHeight > height;
    if (isMediaBiggerThanContainer !== canUseExtraZoom) {
      setCanUseExtraZoom(isMediaBiggerThanContainer);
    }
  }, [canUseExtraZoom]);
  const handlePress = useCallback(() => {
    if (isMaxZoom) handleDismiss();
    else setExtraZoomed(true);
  }, [isMaxZoom, handleDismiss, setExtraZoomed]);

  return (
    <Modal
      active={!!media}
      dismissable
      onDismiss={handleDismiss}
      showDarkOverlay
      overlayOpacity={0.9}
      className="zoomed-in-media-modal"
    >
      {type === 'image' && (
        <MediaContainer maxZoom={isMaxZoom}>
          <MediaWrapper
            extraZoomed={extraZoomed}
            width={media.dimensions?.w}
            height={media.dimensions?.h}
          >
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
            <img
              className={classNames(extraZoomed && 'extra-zoomed')}
              onLoad={handleLoad}
              ref={mediaRef}
              src={media.url}
              alt=""
              onClick={handlePress}
              onKeyPress={handlePress}
            />
          </MediaWrapper>
        </MediaContainer>
      )}
    </Modal>
  );
};
